import React, { DragEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import { CategoryItem } from '../../components/Navigational/LeftMenuComponent';
import { getIconPath } from '../../Functions/GetIconPath';
import { getAccessToken } from '../../Functions/LocalStorageUtils';
import { setShowSaveDetail } from '../../redux/reducers/appReducer';
import { useAppDispatch } from '../../redux/store';

import styles from './leftMenu.module.scss';

interface LeftMenuContextValue {
    isOpen: boolean;
    expandedCategoryId: string | null;
    setExpandedCategoryId: React.Dispatch<React.SetStateAction<string | null>>;
    requestOpenMenu: () => void;
}

const LeftMenuContext = React.createContext<LeftMenuContextValue | null>(null);

export interface LeftMenuPropsI {
    children?: React.ReactNode;
    leftMenuOpen?: boolean;
    onClick: () => void;
}

const LeftMenu = ({ children, leftMenuOpen, onClick }: LeftMenuPropsI): JSX.Element => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(leftMenuOpen ?? false);
    const [expandedCategoryId, setExpandedCategoryId] = useState<string | null>(null);

    useEffect(() => {
        setIsOpen(leftMenuOpen ?? false);
    }, [leftMenuOpen]);

    const handleClick = () => {
        setIsOpen(!isOpen);
        onClick();
    }

    const requestOpenMenu = () => {
        if (!isOpen) {
            handleClick();
        }
    }

    const menuClasses = classNames({
        [styles.leftMenu__open]: isOpen,
        [styles.leftMenu__closed]: !isOpen
    });

    const bottomButtons = [
        {
            icon: 'settings',
            onClick: () => navigate('/settings'),
            label: t('leftMenu.settings')
        },
    ]

    if(!getAccessToken()){
        bottomButtons.push({
            icon: 'login',
            onClick: () => location.href = (process.env.REACT_APP_LOGIN_URL + '/?returnUrl=' + encodeURIComponent(window.location.href) || '/'),
            label: t('leftMenu.login')
        })
    }else{
        bottomButtons.push({
            icon: 'save',
            onClick: () => dispatch(setShowSaveDetail(true)),
            label: t('leftMenu.save')
        });
    }
    
    return (
        <LeftMenuContext.Provider value={{ isOpen, expandedCategoryId, setExpandedCategoryId, requestOpenMenu }}>
            <div id="tutorial-left-menu" style={{ height: '100%' }}>
                <div className={classNames(styles.leftMenu, menuClasses)}>
                    <div onClick={handleClick} className={classNames((isOpen ? styles.leftMenu__top__open : styles.leftMenu__top__closed), styles.leftMenu__top)}>
                        <img style={{ width: '3rem', transition: 'transform 0.3s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} src={getIconPath('arrow_right_black')} alt="Menu Icon"></img>
                    </div> 
                    <div className={styles.leftMenu__container}>
                        <div className={classNames((isOpen ? styles.leftMenu__content__open : styles.leftMenu__content__closed), styles.leftMenu__content)}>
                            {children}
                        </div>
                        <div className={classNames((isOpen ? styles.leftMenu__bottom__open : styles.leftMenu__bottom__closed), styles.leftMenu__bottom)}>
                            {bottomButtons.map((button, index) => (
                                <div key={index} className={styles.leftMenu__bottom__item} onClick={button.onClick}>
                                    <img src={getIconPath(button.icon)} alt={button.label} />
                                    {isOpen && <span>{button.label}</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </LeftMenuContext.Provider>
    );
};

export interface LeftMenuCategoryPropsI {
    item: CategoryItem;
    leftMenuOpen: boolean;
    categoryId: string;
}

const LeftMenuCategory = ({ item, leftMenuOpen, categoryId }: LeftMenuCategoryPropsI): JSX.Element => {
    const menuContext = React.useContext(LeftMenuContext);
    const categoryOpen = menuContext?.expandedCategoryId === categoryId;

    const handleCategoryClick = () => {
        if (!menuContext) {
            return;
        }

        if (!menuContext.isOpen) {
            menuContext.requestOpenMenu();
        }

        menuContext.setExpandedCategoryId(categoryOpen ? null : categoryId);
    }

    const categoryClasses = classNames({
        [styles.leftMenu__category__container__open]: categoryOpen,
        [styles.leftMenu__category__container__closed]: !categoryOpen
    });

    

    const handleItemDragStart = (event: DragEvent<HTMLDivElement>, subItem: CategoryItem) => {
        event.dataTransfer.effectAllowed = 'copy';
        event.dataTransfer.setData('application/floorplan-item', JSON.stringify(subItem));
        event.dataTransfer.setData('application/floorplan-item-type', subItem.type || 'appliance');
        event.dataTransfer.setData('application/floorplan-item-state', 'error');
        event.dataTransfer.setData('text/plain', subItem.title);
        event.dataTransfer.setData('application/floorplan-itemtype', subItem.type || '');
    };

    if(item.type === 'item'){
        return (
            <div className={styles.leftMenu__category}>
                <div className={classNames(styles.leftMenu__category__container, styles.leftMenu__category__container__open)}>
                    {item.items && item.items.map((subItem, subIndex) => (
                        <div
                            className={styles.leftMenu__category__item}
                            key={subIndex}
                            draggable
                            onDragStart={(event) => handleItemDragStart(event, subItem)}
                        >
                            <div className={styles.leftMenu__category__container__item}>
                                <img className={styles.leftMenu__category__container__item__icon} src={getIconPath(subItem.icon)} alt={`${subItem.title} icon`} />
                                {leftMenuOpen && <span>{subItem.title}</span>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    return (
        <div className={styles.leftMenu__category}>
            <div className={styles.leftMenu__category__heading}>
                <img 
                    className={styles.leftMenu__category__heading__icon}
                    src={getIconPath(item.icon)} alt={`${item.title} icon`} 
                    onClick={handleCategoryClick} 
                />
                {leftMenuOpen && <span>{item.title}</span>}
            </div>
            <div className={classNames(styles.leftMenu__category__container, categoryClasses)}>
                {item.items && item.items.map((subItem, subIndex) => (
                    <div
                        className={styles.leftMenu__category__item}
                        key={subIndex}
                        draggable
                        onDragStart={(event) => handleItemDragStart(event, subItem)}
                    >
                        <div className={styles.leftMenu__category__container__item}>
                            <img className={styles.leftMenu__category__container__item__icon} src={getIconPath(subItem.icon)} alt={`${subItem.title} icon`} />
                            {leftMenuOpen && <span>{subItem.title}</span>}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    ); 
}

const LeftMenuCategoryActuators = ({ item, leftMenuOpen, categoryId }: LeftMenuCategoryPropsI): JSX.Element => {
    const menuContext = React.useContext(LeftMenuContext);
    const categoryOpen = menuContext?.expandedCategoryId === categoryId;

    const handleCategoryClick = () => {
        if (!menuContext) {
            return;
        }

        if (!menuContext.isOpen) {
            menuContext.requestOpenMenu();
        }

        menuContext.setExpandedCategoryId(categoryOpen ? null : categoryId);
    }

    const categoryClasses = classNames({
        [styles.leftMenu__category__container__open]: categoryOpen,
        [styles.leftMenu__category__container__closed]: !categoryOpen
    });

    const handleItemDragStart = (event: DragEvent<HTMLDivElement>, subItem: CategoryItem) => {
        event.dataTransfer.effectAllowed = 'copy';
        event.dataTransfer.setData('application/floorplan-item', JSON.stringify(subItem));
        event.dataTransfer.setData('application/floorplan-item-type', 'actuator');
        event.dataTransfer.setData('application/floorplan-item-state', 'error');
        event.dataTransfer.setData('text/plain', subItem.title);
        event.dataTransfer.setData('application/floorplan-itemtype', 'actuator');
    };

    return (
        <div className={styles.leftMenu__category}>
            <div className={styles.leftMenu__category__heading}>
                <img 
                    className={styles.leftMenu__category__heading__icon}
                    src={getIconPath(item.icon, '.svg', 'actuators')} alt={`${item.title} icon`} 
                    onClick={handleCategoryClick} 
                />
                {leftMenuOpen && <span>{item.title}</span>}
            </div>
            <div className={classNames(styles.leftMenu__category__container, categoryClasses)}>
                {item.items && item.items.map((subItem, subIndex) => (
                    <div
                        className={styles.leftMenu__category__item}
                        key={subIndex}
                        draggable
                        onDragStart={(event) => handleItemDragStart(event, subItem)}
                    >
                        <div className={styles.leftMenu__category__container__item}>
                            <img className={styles.leftMenu__category__container__item__icon} src={getIconPath(subItem.icon, '.svg', 'actuators')} alt={`${subItem.title} icon`} />
                            {leftMenuOpen && <span>{subItem.title}</span>}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    ); 
};

const LeftMenuCategoryOthers = ({ item, leftMenuOpen, categoryId }: LeftMenuCategoryPropsI): JSX.Element => {
    const menuContext = React.useContext(LeftMenuContext);
    const categoryOpen = menuContext?.expandedCategoryId === categoryId;

    const handleCategoryClick = () => {
        if (!menuContext) {
            return;
        }

        if (!menuContext.isOpen) {
            menuContext.requestOpenMenu();
        }

        menuContext.setExpandedCategoryId(categoryOpen ? null : categoryId);
    }

    const categoryClasses = classNames({
        [styles.leftMenu__category__container__open]: categoryOpen,
        [styles.leftMenu__category__container__closed]: !categoryOpen
    });

    const handleItemDragStart = (event: DragEvent<HTMLDivElement>, subItem: CategoryItem) => {
        event.dataTransfer.effectAllowed = 'copy';
        event.dataTransfer.setData('application/floorplan-item', JSON.stringify(subItem));
        event.dataTransfer.setData('application/floorplan-item-type', 'other');
        event.dataTransfer.setData('application/floorplan-item-state', 'error');
        event.dataTransfer.setData('text/plain', subItem.title);
        event.dataTransfer.setData('application/floorplan-itemtype', 'other');
    };

    return (
        <div className={styles.leftMenu__category}>
            <div className={styles.leftMenu__category__heading}>
                <img 
                    className={styles.leftMenu__category__heading__icon}
                    src={getIconPath(item.icon, '.svg', 'other')} alt={`${item.title} icon`} 
                    onClick={handleCategoryClick} 
                />
                {leftMenuOpen && <span>{item.title}</span>}
            </div>
            <div className={classNames(styles.leftMenu__category__container, categoryClasses)}>
                {item.items && item.items.map((subItem, subIndex) => (
                    <div
                        className={styles.leftMenu__category__item}
                        key={subIndex}
                        draggable
                        onDragStart={(event) => handleItemDragStart(event, subItem)}
                    >
                        <div className={styles.leftMenu__category__container__item}>
                            <img className={styles.leftMenu__category__container__item__icon} src={getIconPath(subItem.icon, '.svg', 'other')} alt={`${subItem.title} icon`} />
                            {leftMenuOpen && <span>{subItem.title}</span>}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    ); 
}

LeftMenu.Category = LeftMenuCategory;
LeftMenu.CategoryOthers = LeftMenuCategoryOthers;
LeftMenu.CategoryActuators = LeftMenuCategoryActuators;
LeftMenu.Divider = () => <div className={styles.leftMenu__divider} />;


export default LeftMenu;